// components/Auth.js
import { useState } from "react";
import { useNavigate } from "react-router-dom"; // Add this import
import { useTranslation } from "react-i18next";
import { registerUser, loginUser, resetPassword } from "../firebase";
import i18n from "../i18n";

export default function Auth({ onLogin }) {
  const { t } = useTranslation();
  const navigate = useNavigate(); // Add this hook
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");

  const toggleLanguage = () => {
    const newLanguage = i18n.language === "en" ? "el" : "en";
    i18n.changeLanguage(newLanguage);
    localStorage.setItem("language", newLanguage);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    let result;
    if (isLogin) {
      result = await loginUser(email, password);
    } else {
      if (!displayName.trim()) {
        setError(t("auth.password_mismatch"));
        setLoading(false);
        return;
      }
      result = await registerUser(email, password, displayName);
    }

    if (result.success) {
      onLogin(result.user, result.displayName);
      navigate("/"); // Add this line to redirect to dashboard
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetMessage("");
    setLoading(true);

    const result = await resetPassword(resetEmail);

    if (result.success) {
      setResetMessage(t("auth.password_reset_sent")); // Fix this line too
      setResetEmail("");
    } else {
      setResetMessage("Error: " + result.error);
    }
    setLoading(false);
  };

  return (
    <div
      className="min-vh-100 d-flex align-items-center justify-content-center py-4"
      style={{ background: "var(--bg-primary)" }}
    >
      <div className="container-fluid px-3">
        <div className="row justify-content-center">
          <div className="col-12 col-sm-10 col-md-8 col-lg-6 col-xl-5">
            <div
              className="card border-0 shadow-lg mx-auto"
              style={{
                borderRadius: "20px",
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
                maxWidth: "500px",
              }}
            >
              <div className="card-body p-3 p-sm-4 p-md-5">
                <div className="text-center mb-3 mb-md-4">
                  <div className="mb-2 mb-md-0">
                    <img
                      src="/logo.png"
                      alt="Tenekkapp Logo"
                      className="img-fluid"
                      style={{
                        height: "clamp(120px, 25vw, 300px)",
                        width: "auto",
                        maxHeight: "300px",
                      }}
                    />
                  </div>
                  <div className="mb-3 mb-md-2">
                    <button
                      className="btn btn-outline-primary btn-sm rounded-pill px-3"
                      onClick={toggleLanguage}
                      title={t("common.language")}
                    >
                      {i18n.language === "en" ? "ΕΛ" : "EN"}
                    </button>
                  </div>
                  <div className="d-flex justify-content-center mb-3 mb-md-4">
                    <div
                      className="btn-group rounded-pill overflow-hidden border w-100"
                      role="group"
                      style={{ maxWidth: "280px" }}
                    >
                      <button
                        type="button"
                        className={`btn ${isLogin ? "btn-primary" : "btn-outline-primary"} rounded-pill flex-fill`}
                        onClick={() => setIsLogin(true)}
                      >
                        <i className="fas fa-sign-in-alt me-1"></i>
                        <span className="d-none d-sm-inline">
                          {t("auth.login")}
                        </span>
                        <span className="d-sm-none">{t("auth.login")}</span>
                      </button>
                      <button
                        type="button"
                        className={`btn ${!isLogin ? "btn-primary" : "btn-outline-primary"} rounded-pill flex-fill`}
                        onClick={() => setIsLogin(false)}
                      >
                        <i className="fas fa-user-plus me-1"></i>
                        <span className="d-none d-sm-inline">
                          {t("auth.signup")}
                        </span>
                        <span className="d-sm-none">{t("auth.signup")}</span>
                      </button>
                    </div>
                  </div>
                </div>

                {error && (
                  <div
                    className="alert alert-danger alert-dismissible fade show rounded-pill"
                    role="alert"
                  >
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {error}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setError("")}
                    ></button>
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  {!isLogin && (
                    <div className="mb-3 mb-md-4">
                      <label className="form-label fw-semibold">
                        <i className="fas fa-user me-1 text-primary"></i>
                        {t("dashboard.user_name")}
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-light border-end-0">
                          <i className="fas fa-id-card text-muted"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0"
                          placeholder="John Doe"
                          value={displayName}
                          onChange={(e) => setDisplayName(e.target.value)}
                          required
                          style={{ background: "rgba(255, 255, 255, 0.8)" }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="mb-3 mb-md-4">
                    <label className="form-label fw-semibold">
                      <i className="fas fa-envelope me-1 text-primary"></i>
                      {t("auth.email")}
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-light border-end-0">
                        <i className="fas fa-at text-muted"></i>
                      </span>
                      <input
                        type="email"
                        className="form-control border-start-0"
                        placeholder="email@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        style={{ background: "rgba(255, 255, 255, 0.8)" }}
                      />
                    </div>
                  </div>

                  <div className="mb-3 mb-md-4">
                    <label className="form-label fw-semibold">
                      <i className="fas fa-lock me-1 text-primary"></i>
                      {t("auth.password")}
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-light border-end-0">
                        <i className="fas fa-key text-muted"></i>
                      </span>
                      <input
                        type="password"
                        className="form-control border-start-0"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength="6"
                        style={{ background: "rgba(255, 255, 255, 0.8)" }}
                      />
                    </div>
                    {!isLogin && (
                      <small className="text-muted mt-1 d-block">
                        <i className="fas fa-info-circle me-1"></i>Password must
                        be at least 6 characters
                      </small>
                    )}
                  </div>

                  {isLogin && (
                    <div className="text-end mb-2 mb-md-3">
                      <button
                        type="button"
                        className="btn btn-link text-decoration-none p-0 fw-normal"
                        onClick={() => setShowForgotPassword(true)}
                      >
                        <i className="fas fa-key me-1"></i>
                        <span className="d-none d-sm-inline">
                          {t("auth.forgot_password")}
                        </span>
                        <span className="d-sm-none">
                          {t("auth.forgot_password")}
                        </span>
                      </button>
                    </div>
                  )}

                  <button
                    type="submit"
                    className="btn btn-primary w-100 rounded-pill py-2 py-md-3 mb-3 mb-md-4 fw-semibold"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        {t("common.loading")}
                      </>
                    ) : (
                      <>
                        <i
                          className={`fas ${isLogin ? "fa-sign-in-alt" : "fa-user-plus"} me-2`}
                        ></i>
                        <span className="d-none d-sm-inline">
                          {isLogin ? t("auth.login") : t("auth.signup")}
                        </span>
                        <span className="d-sm-none">
                          {isLogin ? t("auth.login") : t("auth.signup")}
                        </span>
                      </>
                    )}
                  </button>

                  <div className="text-center">
                    <button
                      type="button"
                      className="btn btn-link text-decoration-none fw-semibold"
                      onClick={() => {
                        setIsLogin(!isLogin);
                        setError("");
                        setDisplayName("");
                        setEmail("");
                        setPassword("");
                      }}
                    >
                      {isLogin ? (
                        <>
                          <i className="fas fa-user-plus me-1"></i>
                          <span className="d-none d-sm-inline">
                            {t("auth.no_account")}
                          </span>
                          <span className="d-sm-none">
                            {t("auth.no_account")}
                          </span>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-sign-in-alt me-1"></i>
                          <span className="d-none d-sm-inline">
                            {t("auth.have_account")}
                          </span>
                          <span className="d-sm-none">
                            {t("auth.have_account")}
                          </span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered modal-sm mx-3">
            <div
              className="modal-content border-0 shadow-lg"
              style={{ borderRadius: "20px" }}
            >
              <div className="modal-header border-0 pb-0 px-3 px-md-4">
                <div className="text-center w-100">
                  <div className="fs-2 fs-md-1 text-primary mb-2">
                    <i className="fas fa-key"></i>
                  </div>
                  <h5 className="modal-title fw-bold text-primary mb-1">
                    {t("auth.forgot_password")}
                  </h5>
                  <p className="text-muted small mb-0 d-none d-sm-block">
                    Enter your email to receive reset instructions
                  </p>
                  <p className="text-muted small mb-0 d-sm-none">
                    Enter your email
                  </p>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetEmail("");
                    setResetMessage("");
                  }}
                ></button>
              </div>
              <div className="modal-body pt-0 px-3 px-md-4">
                {resetMessage && (
                  <div
                    className={`alert ${resetMessage.includes("Error") ? "alert-danger" : "alert-success"} alert-dismissible fade show rounded-pill mb-3`}
                    role="alert"
                  >
                    <i
                      className={`fas ${resetMessage.includes("Error") ? "fa-exclamation-triangle" : "fa-check-circle"} me-2`}
                    ></i>
                    {resetMessage}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setResetMessage("")}
                    ></button>
                  </div>
                )}

                <form onSubmit={handleForgotPassword}>
                  <div className="mb-3 mb-md-4">
                    <label className="form-label fw-semibold">
                      <i className="fas fa-envelope me-1 text-primary"></i>
                      {t("auth.email")}
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-light border-end-0">
                        <i className="fas fa-at text-muted"></i>
                      </span>
                      <input
                        type="email"
                        className="form-control border-start-0"
                        placeholder="email@example.com"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        required
                        style={{ background: "rgba(255, 255, 255, 0.8)" }}
                      />
                    </div>
                  </div>

                  <div className="d-flex flex-column flex-sm-row gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary rounded-pill order-2 order-sm-1"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setResetEmail("");
                        setResetMessage("");
                      }}
                    >
                      <i className="fas fa-times me-1"></i>
                      <span className="d-none d-sm-inline">Cancel</span>
                      <span className="d-sm-none">Cancel</span>
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary rounded-pill order-1 order-sm-2"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Sending...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane me-1"></i>
                          <span className="d-none d-sm-inline">Send</span>
                          <span className="d-sm-none">Send</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
